import { FormEvent, useEffect, useMemo, useState } from "react";
import { AiOutlineCloseCircle } from "react-icons/ai";
import styled from "styled-components";
import { format } from "date-fns";
import Modal from "../../common/Modal";
import InputDropdown from "../../common/InputDropdown";
import { useSeller } from "../../../context/seller";
import { useGlobal } from "../../../context/global";
import SellerService from "../../../services/seller.service";
import * as CommonTypes from "../../../types/common.types";

// Botón "Agregar tour" junto al calendario (17/10/2026). Reemplaza al "+" de cada tour: se busca al
// pasajero, se responde lo mismo que preguntaba el "+" (si es parte de una reserva que ya tiene y quién
// más va) y se sigue con el flujo de la venta en grupo: tour, precio y entradas, pago al final y voucher.

export function joinKey(option: CommonTypes.JoinOption) {
    return option.type === 'group' ? `g:${option.group_id}` : `r:${option.reservation_id}`
}

// "Juan, Carlos · Valle 12/10, Cejar 13/10": quiénes y qué tours, para reconocer el grupo de un vistazo.
export function joinLabel(option: CommonTypes.JoinOption) {
    const tours = option.tours.map(t => `${t.tour_id} ${t.date.slice(8, 10)}/${t.date.slice(5, 7)}`).join(', ')
    return `${option.passengers.join(', ')} · ${tours}`
}

export default function AgregarTour() {
    const { setIsLoading, setMessage } = useGlobal()
    const { setIsAgregarTourOpen, setNewGroup, setNewGroupReservationData, setNewGroupReservations, setSummary, currentDay } = useSeller()
    const [passengers, setPassengers] = useState<CommonTypes.Passenger[]>([])
    const [passengerId, setPassengerId] = useState('')
    const [opciones, setOpciones] = useState<CommonTypes.JoinOption[]>([])
    // 'none' | 'g:<group_id>' | 'r:<reservation_id>'
    const [eleccion, setEleccion] = useState('none')
    const [extras, setExtras] = useState<string[]>([])
    const dia = format(currentDay, 'yyyy-MM-dd')

    useEffect(() => {
        setIsLoading(true)
        SellerService.listPassengers()
            .then(res => setPassengers(res.data))
            .catch(error => console.log(error))
            .finally(() => setIsLoading(false))
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setExtras([])
        if (!passengerId) { setOpciones([]); setEleccion('none'); return }
        let vigente = true
        SellerService.listJoinOptions(passengerId, dia)
            .then(res => {
                if (!vigente) return
                const ops: CommonTypes.JoinOption[] = res.data
                setOpciones(ops)
                // La primera es la más cercana a la fecha: casi siempre el viaje actual.
                setEleccion(ops.length ? joinKey(ops[0]) : 'none')
            })
            .catch(() => { if (vigente) { setOpciones([]); setEleccion('none') } })
        return () => { vigente = false }
    }, [passengerId, dia])

    useEffect(() => { setExtras([]) }, [eleccion])

    const pasajero = passengers.find(p => p.id === passengerId)
    const grupo = opciones.find(o => joinKey(o) === eleccion)
    const candidatos = useMemo(() => (grupo?.members || []).filter(m => m.id !== passengerId), [grupo, passengerId])

    async function onSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        if (!pasajero) {
            setMessage({ type: 'error', content: 'Busca y elige al pasajero.' })
            setTimeout(() => setMessage(undefined), 4000)
            return
        }
        setIsLoading(true)
        try {
            const van: Partial<CommonTypes.Passenger>[] = [
                pasajero,
                ...candidatos.filter(c => extras.includes(c.id)).map(c => passengers.find(p => p.id === c.id) || { id: c.id, name: c.name }),
            ]
            let datos: any = {}
            if (eleccion.startsWith('g:')) datos = { group_id: eleccion.slice(2), tours_existentes: grupo?.tours || [] }
            else if (eleccion.startsWith('r:')) datos = { join_reservation_id: eleccion.slice(2), tours_existentes: grupo?.tours || [] }
            else datos = { group_id: (await SellerService.createGroup()).data.id }
            // Venta nueva: nada de una venta anterior en el resumen ni en el voucher.
            setSummary([])
            setNewGroupReservations([])
            setNewGroupReservationData(datos)
            setNewGroup(van)
            setIsAgregarTourOpen(false)
        } catch (error) {
            console.log(error)
            setMessage({ type: 'error', content: 'Ocurrió un error, intenta de nuevo.' })
            setTimeout(() => setMessage(undefined), 4000)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal onSubmit={onSubmit}>
            <h1>Agregar tour</h1>
            <p style={{fontSize: 13, color: "#475569", margin: "4px 0 8px"}}>Para un pasajero que ya existe. Si es nuevo, usa "Crear grupo".</p>
            <InputDropdown
            label="Pasajero"
            options={passengers}
            keys={['name', 'email', 'id']}
            selector="id"
            value={pasajero?.name || ''}
            optionDefaultHeight={80}
            style={{fontSize: '14px', zIndex: 2000}}
            onChange={(id: string) => setPassengerId(id)}
            />
            {
                pasajero && opciones.length > 0 &&
                <Pregunta>
                    <legend>1. ¿Este tour es parte de una reserva que {pasajero.name} ya tiene?</legend>
                    <p style={{fontSize: 13, color: "#475569", margin: "4px 0 8px"}}>Si es la misma, queda todo junto y los pagos se ven en una sola cuenta.</p>
                    {
                        opciones.map(o => (
                            <label key={joinKey(o)}>
                                <input type="radio" name="join" checked={eleccion === joinKey(o)} onChange={() => setEleccion(joinKey(o))} />
                                <span>Sí, a la de: {joinLabel(o)}</span>
                            </label>
                        ))
                    }
                    <label>
                        <input type="radio" name="join" checked={eleccion === 'none'} onChange={() => setEleccion('none')} />
                        <span>No, es una reserva nueva aparte</span>
                    </label>
                </Pregunta>
            }
            {
                candidatos.length > 0 &&
                <Pregunta>
                    <legend>2. ¿Quién más de esa reserva va?</legend>
                    <p style={{fontSize: 13, color: "#475569", margin: "4px 0 8px"}}>Marca a los que van con {pasajero?.name}. A cada uno se le crea su reserva.</p>
                    {
                        candidatos.map(c => (
                            <label key={c.id}>
                                <input type="checkbox" checked={extras.includes(c.id)}
                                onChange={e => setExtras(prev => e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))} />
                                <span>{c.name}</span>
                            </label>
                        ))
                    }
                </Pregunta>
            }
            <div className="actions">
                <button type="submit">Continuar: elegir tour</button>
            </div>
            <div className="close" title="close" onClick={() => setIsAgregarTourOpen(false)}>
                <AiOutlineCloseCircle/>
            </div>
        </Modal>
    )
}

const Pregunta = styled.fieldset`
    margin-top: 16px;
    padding: 10px 12px;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    background-color: #eff6ff;
    max-height: 200px;
    overflow-y: auto;
    legend { padding: 0 4px; font-size: 14px; font-weight: 600; }
    label { display: flex; align-items: flex-start; gap: 8px; padding: 4px 0; font-size: 13px; line-height: 1.35; cursor: pointer; }
    input { margin-top: 2px; flex-shrink: 0; }
`
